import { PageIdRequestV2 } from '~/api-services/ReportsService/types';
import { csvHeader, csvRow } from './csv';

export type ExportContext = { currency: string };

export type ExportColumn<T> = {
    header: string;
    value: (row: T, ctx: ExportContext) => string;
};

export type ExportPage<T> = {
    data: T[];
    lastPageId: PageIdRequestV2;
    hasNextPage: boolean;
};

export type CollectArgs<T> = {
    fetchPage: (lastPageId: PageIdRequestV2 | null, pageSize: number, signal: AbortSignal) => Promise<ExportPage<T>>;
    columns: ExportColumn<T>[];
    context: ExportContext;
    headers: string[];
    pageSize: number;
    maxRows: number;
    signal: AbortSignal;
    onProgress?: (rows: number) => void;
    onPageDone?: () => Promise<void>;
};

export type CollectResult = { lines: string[]; truncated: boolean };

const sameCursor = (a: PageIdRequestV2 | null, b: PageIdRequestV2 | null) =>
    !!a && !!b && a.id === b.id && a.timestamp === b.timestamp;

export const collectCsvLines = async <T>({
    fetchPage,
    columns,
    context,
    headers,
    pageSize,
    maxRows,
    signal,
    onProgress,
    onPageDone,
}: CollectArgs<T>): Promise<CollectResult> => {
    const lines: string[] = [csvHeader(headers)];
    const maxPages = Math.ceil(maxRows / pageSize) + 1;
    let cursor: PageIdRequestV2 | null = null;
    let pages = 0;
    let truncated = false;

    for (;;) {
        const page = await fetchPage(cursor, pageSize, signal);
        const rows = page?.data || [];

        for (const row of rows) {
            lines.push(csvRow(columns.map((c) => c.value(row, context))));
        }
        onProgress?.(lines.length - 1);

        const finished = !page?.hasNextPage || rows.length === 0;

        if (lines.length - 1 >= maxRows) {
            lines.length = maxRows + 1;
            truncated = !finished;
            onProgress?.(maxRows);
            break;
        }
        if (finished) {
            break;
        }
        if (sameCursor(page.lastPageId, cursor)) {
            break;
        }
        if (++pages > maxPages) {
            break;
        }

        cursor = page.lastPageId;
        if (onPageDone) {
            await onPageDone();
        }
    }

    return { lines, truncated };
};
